import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import type {
  Article,
  ArticleDetail,
  ArticleGenerateRequest,
  ArticleCreate,
  ArticleUpdate,
  GenerationProgressResponse,
  PaginatedResponse,
  Site,
  SiteDetail,
  SiteCreate,
  SiteUpdate,
  SiteCredentialsUpdate,
  ConnectionTestResponse,
  CategoriesResponse,
  PublishRequest,
  PublishResponse,
  ScoreRequest,
  ScoreResponse,
  AnalyticsResponse,
  StatsOverview,
  Schedule,
  ScheduleCreate,
  ScheduleUpdate,
  ScheduleStats,
  CalendarResponse,
  Batch,
  BatchDetail,
  BatchArticle,
  BatchSummary,
  CSVValidationResponse,
} from "@/types";
// Mock data removed - always use real API

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Token is set by authStore when logging in
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Check if we have a refresh token before trying to refresh
        const { useAuthStore } = await import("@/stores/authStore");
        const { refreshToken, isAuthenticated } = useAuthStore.getState();

        // If not authenticated or no refresh token, just redirect to login
        if (!isAuthenticated || !refreshToken) {
          useAuthStore.getState().logout();
          window.location.href = "/login";
          return Promise.reject(error);
        }

        // Try to refresh the token
        const { refreshAccessToken } = useAuthStore.getState();
        await refreshAccessToken();

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout
        const { useAuthStore } = await import("@/stores/authStore");
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// API helper functions
export const apiGet = <T>(url: string, config = {}) =>
  api.get<T>(url, config).then((res) => res.data);

export const apiPost = <T>(url: string, data = {}, config = {}) =>
  api.post<T>(url, data, config).then((res) => res.data);

export const apiPut = <T>(url: string, data = {}, config = {}) =>
  api.put<T>(url, data, config).then((res) => res.data);

export const apiPatch = <T>(url: string, data = {}, config = {}) =>
  api.patch<T>(url, data, config).then((res) => res.data);

export const apiDelete = <T>(url: string, config = {}) =>
  api.delete<T>(url, config).then((res) => res.data);

// =============================================================================
// Articles API
// =============================================================================

export const articlesApi = {
  /**
   * List articles with pagination
   */
  list: (params?: {
    page?: number;
    page_size?: number;
    site_id?: string;
    status?: string;
    tag?: string;
  }) => {
    return apiGet<PaginatedResponse<Article>>("/api/articles", { params });
  },

  /**
   * Get single article with full details
   */
  get: (articleId: string) => {
    return apiGet<ArticleDetail>(`/api/articles/${articleId}`);
  },

  /**
   * Create a new article manually
   */
  create: (data: ArticleCreate) => {
    return apiPost<Article>("/api/articles", data);
  },

  /**
   * Update an article
   */
  update: (articleId: string, data: ArticleUpdate) => {
    return apiPatch<Article>(`/api/articles/${articleId}`, data);
  },

  /**
   * Delete an article
   */
  delete: (articleId: string) => {
    return apiDelete<void>(`/api/articles/${articleId}`);
  },

  /**
   * Start article generation with AI agents
   */
  generate: (data: ArticleGenerateRequest) => {
    return apiPost<Article>("/api/articles/generate", data);
  },

  /**
   * Get generation progress for an article
   */
  getProgress: (articleId: string) => {
    return apiGet<GenerationProgressResponse>(
      `/api/articles/${articleId}/progress`,
    );
  },

  /**
   * Retry failed generation
   */
  retry: (articleId: string, fromAgent?: string) =>
    apiPost<Article>(
      `/api/articles/${articleId}/retry`,
      {},
      { params: { from_agent: fromAgent } },
    ),

  /**
   * Calculate SEO/GEO scores for an article
   */
  score: (articleId: string, data?: ScoreRequest) =>
    apiPost<ScoreResponse>(`/api/articles/${articleId}/score`, data),

  /**
   * Publish article to WordPress
   */
  publish: (articleId: string, data: PublishRequest) =>
    apiPost<PublishResponse>(`/api/articles/${articleId}/publish`, data),

  /**
   * Get the original generation config for an article (for duplicate/re-generate).
   */
  getGenerationConfig: (articleId: string) =>
    apiGet<{ generation_config: Record<string, unknown> }>(
      `/api/articles/${articleId}/generation-config`,
    ),

  /**
   * Regenerate just the featured image for an existing article.
   * Returns immediately with a task_id; poll article.image_url to see when it updates.
   */
  regenerateImage: (articleId: string, data: { image_instructions?: string }) =>
    apiPost<{ success: boolean; task_id: string | null; message: string }>(
      `/api/articles/${articleId}/regenerate-image`,
      data,
    ),

  /**
   * Get content calendar data
   */
  getCalendar: (startDate: string, endDate: string, siteId?: string) =>
    apiGet<CalendarResponse>("/api/articles/calendar", {
      params: { start_date: startDate, end_date: endDate, site_id: siteId },
    }),
};

// =============================================================================
// Batches API
// =============================================================================

export const batchesApi = {
  /**
   * Validate CSV before creating batch
   */
  validateCSV: (file: File, siteId: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("site_id", siteId);
    return api
      .post<CSVValidationResponse>("/api/batches/validate-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  /**
   * Create batch from CSV file
   */
  create: (
    file: File,
    siteId: string,
    options?: {
      name?: string;
      priority?: number;
      default_word_count?: number;
      default_tone?: string;
      default_language?: string;
      default_include_image?: boolean;
    },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("site_id", siteId);
    if (options?.name) formData.append("name", options.name);
    if (options?.priority)
      formData.append("priority", options.priority.toString());
    if (options?.default_word_count)
      formData.append(
        "default_word_count",
        options.default_word_count.toString(),
      );
    if (options?.default_tone)
      formData.append("default_tone", options.default_tone);
    if (options?.default_language)
      formData.append("default_language", options.default_language);
    if (options?.default_include_image !== undefined)
      formData.append(
        "default_include_image",
        options.default_include_image.toString(),
      );
    return api
      .post<BatchDetail>("/api/batches", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  /**
   * List batches with pagination
   */
  list: (params?: {
    page?: number;
    page_size?: number;
    site_id?: string;
    status?: string;
  }) => apiGet<PaginatedResponse<Batch>>("/api/batches", { params }),

  /**
   * Get batch details
   */
  get: (batchId: string) => apiGet<BatchDetail>(`/api/batches/${batchId}`),

  /**
   * Get articles in a batch
   */
  getArticles: (batchId: string, status?: string) =>
    apiGet<BatchArticle[]>(`/api/batches/${batchId}/articles`, {
      params: { status },
    }),

  /**
   * Get batch summary
   */
  getSummary: (batchId: string) =>
    apiGet<BatchSummary>(`/api/batches/${batchId}/summary`),

  /**
   * Cancel a batch
   */
  cancel: (batchId: string) => apiPost<Batch>(`/api/batches/${batchId}/cancel`),

  /**
   * Retry failed articles as new batch
   */
  retryFailed: (batchId: string) =>
    apiPost<BatchDetail>(`/api/batches/${batchId}/retry-failed`),

  /**
   * Delete a batch
   */
  delete: (batchId: string) => apiDelete<void>(`/api/batches/${batchId}`),
};

// =============================================================================
// Sites API
// =============================================================================

export const sitesApi = {
  /**
   * List all sites
   */
  list: (params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
  }) => {
    return apiGet<PaginatedResponse<Site>>("/api/sites", { params });
  },

  /**
   * Get single site with details
   */
  get: (siteId: string) => {
    return apiGet<SiteDetail>(`/api/sites/${siteId}`);
  },

  /**
   * Create a new site
   */
  create: (data: SiteCreate) => apiPost<Site>("/api/sites", data),

  /**
   * Update site settings
   */
  update: (siteId: string, data: SiteUpdate) =>
    apiPatch<Site>(`/api/sites/${siteId}`, data),

  /**
   * Update WordPress credentials
   */
  updateCredentials: (siteId: string, data: SiteCredentialsUpdate) =>
    apiPut<Site>(`/api/sites/${siteId}/credentials`, data),

  /**
   * Delete a site
   */
  delete: (siteId: string) => apiDelete<void>(`/api/sites/${siteId}`),

  /**
   * Test WordPress connection
   */
  testConnection: (siteId: string) =>
    apiPost<ConnectionTestResponse>(`/api/sites/${siteId}/test-connection`),

  /**
   * Activate a site
   */
  activate: (siteId: string) => apiPost<Site>(`/api/sites/${siteId}/activate`),

  /**
   * Deactivate a site
   */
  deactivate: (siteId: string) =>
    apiPost<Site>(`/api/sites/${siteId}/deactivate`),

  /**
   * Get WordPress categories for a site
   */
  getCategories: (siteId: string) =>
    apiGet<CategoriesResponse>(`/api/sites/${siteId}/categories`),
};

// =============================================================================
// Analytics API
// =============================================================================

export const analyticsApi = {
  /**
   * Get full analytics data for dashboard
   */
  getAnalytics: (_days?: number) => {
    return apiGet<AnalyticsResponse>("/api/analytics", {
      params: { days: _days },
    });
  },

  /**
   * Get overview stats only
   */
  getOverview: () => {
    return apiGet<StatsOverview>("/api/analytics/overview");
  },
};

// =============================================================================
// Schedules API
// =============================================================================

export const schedulesApi = {
  /**
   * List all schedules with pagination
   */
  list: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    site_id?: string;
  }) => {
    return apiGet<PaginatedResponse<Schedule>>("/api/schedules", { params });
  },

  /**
   * Get schedule statistics
   */
  getStats: () => {
    return apiGet<ScheduleStats>("/api/schedules/stats");
  },

  /**
   * Get a single schedule
   */
  get: (scheduleId: string) => {
    return apiGet<Schedule>(`/api/schedules/${scheduleId}`);
  },

  /**
   * Create a new schedule
   */
  create: (data: ScheduleCreate) => {
    return apiPost<Schedule>("/api/schedules", data);
  },

  /**
   * Update a schedule
   */
  update: (scheduleId: string, data: ScheduleUpdate) => {
    return apiPatch<Schedule>(`/api/schedules/${scheduleId}`, data);
  },

  /**
   * Delete a schedule
   */
  delete: (scheduleId: string) => {
    return apiDelete<void>(`/api/schedules/${scheduleId}`);
  },

  /**
   * Pause a schedule
   */
  pause: (scheduleId: string) => {
    return apiPost<Schedule>(`/api/schedules/${scheduleId}/pause`);
  },

  /**
   * Resume a paused schedule
   */
  resume: (scheduleId: string) => {
    return apiPost<Schedule>(`/api/schedules/${scheduleId}/resume`);
  },

  /**
   * Run a schedule immediately
   */
  runNow: (scheduleId: string) => {
    return apiPost<{
      message: string;
      schedule_id: string;
      task_ids: string[];
    }>(`/api/schedules/${scheduleId}/run-now`);
  },
};

// =============================================================================
// Auth API (used directly by authStore, but exposed for convenience)
// =============================================================================

export const authApi = {
  /**
   * Get current user
   */
  me: () => apiGet<import("@/types").User>("/api/auth/me"),

  /**
   * Change password
   */
  changePassword: (data: { current_password: string; new_password: string }) =>
    apiPost<{ message: string }>("/api/auth/change-password", data),
};

// =============================================================================
// Agent Configuration Types
// =============================================================================

interface BaseAgentConfig {
  enabled: boolean;
  model: string;
  temperature: number;
  system_prompt_override?: string | null;
  additional_instructions?: string | null;
}

export interface AgentConfiguration {
  presets: Array<{ id: string; name: string; description: string }>;
  quality_targets: {
    min_seo_score: number;
    min_geo_score: number;
    min_human_score: number;
    max_iterations: number;
    min_word_count: number;
    max_total_retries: number;
    require_image: boolean;
    auto_retry_on_failure: boolean;
  };
  research: BaseAgentConfig & {
    max_sources: number;
    search_depth: string;
    include_academic: boolean;
    include_competitor_analysis: boolean;
  };
  writer: BaseAgentConfig & {
    default_word_count: number;
    tone: string;
    writing_style: string;
    target_word_count: number;
    faq_count: number;
    include_faq: boolean;
    use_subheadings: boolean;
  };
  seo: BaseAgentConfig & {
    optimization_level: string;
    target_keyword_density: number;
    meta_title_max_length: number;
    meta_description_max_length: number;
    keyword_density_target: number;
    optimize_headings: boolean;
    optimize_meta: boolean;
    add_internal_links: boolean;
    identify_entity_gaps: boolean;
  };
  geo: BaseAgentConfig & {
    optimization_level: string;
    citation_style: string;
    min_citations: number;
    authoritative_sources: boolean;
    faq_count: number;
    enable_quotable_analysis: boolean;
    enable_fluff_detection: boolean;
    enable_passage_markers: boolean;
    schema_optimization: boolean;
  };
  merger: BaseAgentConfig & {
    strategy: string;
    seo_weight: number;
    geo_weight: number;
    preserve_flow: boolean;
    handle_conflicts: string;
    resolve_conflicts: boolean;
    prioritize_user_experience: boolean;
    add_faq_section: boolean;
    remove_identified_fluff: boolean;
    apply_all_meta: boolean;
    preserve_keyword_density: boolean;
  };
  editor: BaseAgentConfig & {
    check_grammar: boolean;
    check_readability: boolean;
    check_tone: boolean;
    target_reading_level: string;
    check_facts: boolean;
    improve_flow: boolean;
  };
  humanizer: BaseAgentConfig & {
    target_score: number;
    preserve_keywords: boolean;
    style_variation: string;
    humanization_level: string;
    target_human_score: number;
    rewrite_if_below_target: boolean;
    vary_sentence_structure: boolean;
  };
  image_generator: BaseAgentConfig & {
    provider: string;
    style: string;
    size: string;
    auto_alt_text: boolean;
    quality: string;
    default_prompt_template: string;
  };
}

// =============================================================================
// Settings API (stub - to be implemented with backend)
// =============================================================================

const defaultAgentConfiguration: AgentConfiguration = {
  presets: [
    {
      id: "1",
      name: "balanced",
      description: "Balanced SEO and GEO optimization",
    },
    { id: "2", name: "seo_focus", description: "Prioritize traditional SEO" },
    {
      id: "3",
      name: "geo_focus",
      description: "Prioritize AI search optimization",
    },
  ],
  quality_targets: {
    min_seo_score: 80,
    min_geo_score: 80,
    min_human_score: 85,
    max_iterations: 3,
    min_word_count: 1000,
    max_total_retries: 5,
    require_image: true,
    auto_retry_on_failure: true,
  },
  research: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.3,
    max_sources: 10,
    search_depth: "comprehensive",
    include_academic: true,
    include_competitor_analysis: true,
  },
  writer: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.7,
    default_word_count: 1500,
    tone: "professional",
    writing_style: "informative",
    target_word_count: 1500,
    faq_count: 5,
    include_faq: true,
    use_subheadings: true,
  },
  seo: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.3,
    optimization_level: "high",
    target_keyword_density: 1.5,
    meta_title_max_length: 60,
    meta_description_max_length: 160,
    keyword_density_target: 1.5,
    optimize_headings: true,
    optimize_meta: true,
    add_internal_links: true,
    identify_entity_gaps: true,
  },
  geo: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.3,
    optimization_level: "high",
    citation_style: "inline",
    min_citations: 3,
    authoritative_sources: true,
    faq_count: 5,
    enable_quotable_analysis: true,
    enable_fluff_detection: true,
    enable_passage_markers: true,
    schema_optimization: true,
  },
  merger: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.5,
    strategy: "interleave",
    seo_weight: 0.5,
    geo_weight: 0.5,
    preserve_flow: true,
    handle_conflicts: "merge",
    resolve_conflicts: true,
    prioritize_user_experience: true,
    add_faq_section: true,
    remove_identified_fluff: true,
    apply_all_meta: true,
    preserve_keyword_density: true,
  },
  editor: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.3,
    check_grammar: true,
    check_readability: true,
    check_tone: true,
    target_reading_level: "grade-8",
    check_facts: true,
    improve_flow: true,
  },
  humanizer: {
    enabled: true,
    model: "gpt-4o",
    temperature: 0.8,
    target_score: 85,
    preserve_keywords: true,
    style_variation: "medium",
    humanization_level: "medium",
    target_human_score: 85,
    rewrite_if_below_target: true,
    vary_sentence_structure: true,
  },
  image_generator: {
    enabled: true,
    model: "flux-2-pro",
    temperature: 0.7,
    provider: "fal",
    style: "natural",
    size: "1536x1024",
    auto_alt_text: true,
    quality: "medium",
    default_prompt_template: "Create a professional image for: {topic}",
  },
};

export const settingsApi = {
  /**
   * Get agent configuration
   */
  getConfiguration: (): Promise<AgentConfiguration> =>
    Promise.resolve(defaultAgentConfiguration),

  /**
   * Get available presets
   */
  getPresets: (): Promise<
    Array<{ id: string; name: string; description: string }>
  > => Promise.resolve(defaultAgentConfiguration.presets),

  /**
   * Update agent configuration
   */
  updateConfiguration: (
    config: Partial<AgentConfiguration>,
  ): Promise<AgentConfiguration> =>
    Promise.resolve({ ...defaultAgentConfiguration, ...config }),

  /**
   * Reset agent configuration to defaults
   */
  resetConfiguration: (): Promise<AgentConfiguration> =>
    Promise.resolve(defaultAgentConfiguration),

  /**
   * Create a new preset
   */
  createPreset: (preset: {
    name: string;
  }): Promise<{ name: string; description: string }> =>
    Promise.resolve({
      name: preset.name,
      description: `Custom preset: ${preset.name}`,
    }),

  /**
   * Load a preset
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadPreset: (_name: string): Promise<AgentConfiguration> =>
    Promise.resolve(defaultAgentConfiguration),

  /**
   * Delete preset
   */
  deletePreset: (presetName: string): Promise<{ deleted: string }> =>
    Promise.resolve({ deleted: presetName }),
};

export interface SystemHealth {
  status: "ready" | "degraded";
  timestamp: string;
  checks: {
    redis: { status: "ok" | "error"; message: string };
    celery: { status: "ok" | "error"; message: string; workers: number };
  };
}

export const healthApi = {
  /**
   * Check if system is ready for article generation
   * Returns health status of Redis and Celery workers
   */
  checkReady: async (): Promise<SystemHealth> => {
    const response = await api.get<SystemHealth>("/api/health/ready");
    return response.data;
  },

  /**
   * Check specifically if Celery workers are available
   * Throws 503 error if not available
   */
  checkCelery: async (): Promise<{ status: string; workers: number }> => {
    const response = await api.get("/api/health/celery");
    return response.data;
  },
};
